(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const routes = window.ppTheme?.routes || {};
  const strings = window.ppTheme?.strings || {};
  const state = { cart: null, toastTimer: null, searchController: null };

  const escapeHTML = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  function formatMoney(cents, format = window.ppTheme?.moneyFormat || '₱{{ amount }}') {
    const value = Number(cents || 0);
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(value);
    let amount;
    switch (match[1]) {
      case 'amount_no_decimals': amount = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': amount = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = formatWithDelimiters(value, 0, '.', ','); break;
      default: amount = formatWithDelimiters(value, 2); break;
    }
    return format.replace(match[0], amount);
  }

  function showToast(message) {
    const toast = $('#ThemeToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(state.toastTimer);
    state.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }

  function lockBody(locked) {
    const anyOpen = locked || $('.drawer.is-open, .search-modal.is-open, .filter-drawer.is-open, .age-gate.is-open, .move-popup.is-open, .mobile-nav[open]');
    document.body.classList.toggle('is-locked', Boolean(anyOpen));
  }

  function setModal(modal, open) {
    if (!modal) return;
    modal.classList.toggle('is-open', open);
    modal.setAttribute('aria-hidden', String(!open));
    lockBody(open);
  }

  function openCart() {
    setModal($('#CartDrawer'), true);
    refreshCart();
  }

  function closeCart() {
    setModal($('#CartDrawer'), false);
  }

  function openSearch() {
    const modal = $('#SearchModal');
    setModal(modal, true);
    setTimeout(() => $('[data-predictive-search-input]', modal)?.focus(), 80);
  }

  function closeSearch() {
    setModal($('#SearchModal'), false);
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    state.cart = await response.json();
    return state.cart;
  }

  function updateShippingProgress(cart) {
    const threshold = Number(window.ppTheme?.freeShippingThreshold || 0);
    $$('[data-free-shipping-wrap]').forEach((wrap) => {
      if (!threshold) { wrap.hidden = true; return; }
      const remaining = Math.max(threshold - cart.total_price, 0);
      const progress = Math.min((cart.total_price / threshold) * 100, 100);
      const message = $('[data-free-shipping-message]', wrap);
      const bar = $('[data-free-shipping-progress]', wrap);
      wrap.hidden = false;
      if (message) message.innerHTML = remaining > 0
        ? `Add <strong>${escapeHTML(formatMoney(remaining))}</strong> for free shipping.`
        : '<strong>You qualify for free shipping.</strong>';
      if (bar) bar.style.width = `${progress}%`;
    });
  }

  function renderCartDrawer(cart) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    $$('[data-cart-count-inline]').forEach((el) => { el.textContent = `(${cart.item_count})`; });
    const itemsWrap = $('[data-cart-drawer-items]');
    const footer = $('[data-cart-drawer-footer]');
    if (!itemsWrap || !footer) return;

    if (!cart.items.length) {
      itemsWrap.innerHTML = `
        <div class="empty-state">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>
          <h3>Your bag is empty</h3>
          <p>Explore best sellers selected for discreet delivery.</p>
          <a class="button" href="${escapeHTML(window.Shopify?.routes?.root || '/')}collections/best-sellers">Shop best sellers</a>
        </div>`;
      footer.hidden = true;
      updateShippingProgress(cart);
      return;
    }

    itemsWrap.innerHTML = cart.items.map((item, index) => {
      const options = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
      const image = item.image ? `<img src="${escapeHTML(item.image)}&width=180" alt="${escapeHTML(item.product_title)}" width="82" height="82" loading="lazy">` : '';
      return `
        <article class="drawer-cart-item" data-cart-line-item="${index + 1}">
          <a href="${escapeHTML(item.url)}">${image}</a>
          <div>
            <h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>
            ${options}
            <strong>${escapeHTML(formatMoney(item.final_line_price))}</strong>
            <div class="drawer-cart-item__quantity">
              <button type="button" data-cart-change="minus" data-line="${index + 1}" aria-label="Decrease quantity">−</button>
              <input type="number" min="0" value="${item.quantity}" data-cart-quantity-input data-line="${index + 1}" aria-label="Quantity for ${escapeHTML(item.product_title)}">
              <button type="button" data-cart-change="plus" data-line="${index + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <button class="drawer-cart-item__remove" type="button" data-cart-remove data-line="${index + 1}">Remove</button>
        </article>`;
    }).join('');

    $('[data-cart-subtotal]', footer).textContent = formatMoney(cart.total_price);
    footer.hidden = false;
    updateShippingProgress(cart);
  }

  async function refreshCart() {
    try {
      const cart = await fetchCart();
      renderCartDrawer(cart);
      return cart;
    } catch (error) {
      console.error(error);
      showToast(strings.error || 'Something went wrong.');
      return null;
    }
  }

  async function addToCart(payload, sourceButton) {
    const original = sourceButton?.innerHTML;
    if (sourceButton) {
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-busy', 'true');
    }
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: payload
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add product');
      await refreshCart();
      showToast(strings.added || 'Added to your bag');
      setModal($('#CartDrawer'), true);
    } catch (error) {
      console.error(error);
      showToast(strings.error || 'Something went wrong. Please try again.');
    } finally {
      if (sourceButton) {
        sourceButton.disabled = false;
        sourceButton.removeAttribute('aria-busy');
        if (original) sourceButton.innerHTML = original;
      }
    }
  }

  async function addMultipleToCart(items, sourceButton) {
    const original = sourceButton?.innerHTML;
    if (sourceButton) {
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-busy', 'true');
    }
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add products');
      await refreshCart();
      showToast(strings.added || 'Added to your bag');
      setModal($('#CartDrawer'), true);
    } catch (error) {
      console.error(error);
      showToast(strings.error || 'Something went wrong. Please try again.');
    } finally {
      if (sourceButton) {
        sourceButton.disabled = false;
        sourceButton.removeAttribute('aria-busy');
        if (original) sourceButton.innerHTML = original;
      }
    }
  }

  function renderCartPage(cart) {
    const itemsWrap = $('[data-cart-page-items]');
    if (!itemsWrap) return;

    if (!cart.items.length) {
      window.location.reload();
      return;
    }

    itemsWrap.innerHTML = cart.items.map((item, index) => {
      const line = index + 1;
      const variantLine = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !key.startsWith('_'))
        .map(([key, value]) => `<p>${escapeHTML(key)}: ${escapeHTML(value)}</p>`).join('');
      const image = item.image ? `<img src="${escapeHTML(item.image)}&width=320" alt="${escapeHTML(item.product_title)}" loading="lazy">` : '';
      const compare = item.original_line_price !== item.final_line_price ? `<s>${escapeHTML(formatMoney(item.original_line_price))}</s>` : '';
      return `
        <article class="cart-item" data-cart-line-item="${line}">
          <a class="cart-item__image" href="${escapeHTML(item.url)}">${image}</a>
          <div class="cart-item__details">
            <h2><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h2>
            ${variantLine}
            ${properties}
            <div class="cart-item__actions">
              <div class="quantity-selector">
                <button type="button" data-cart-change="minus" data-line="${line}" aria-label="Decrease quantity">−</button>
                <input type="number" name="updates[]" value="${item.quantity}" min="0" data-cart-quantity-input data-line="${line}" aria-label="Quantity for ${escapeHTML(item.product_title)}">
                <button type="button" data-cart-change="plus" data-line="${line}" aria-label="Increase quantity">+</button>
              </div>
              <button class="text-link" type="button" data-cart-remove data-line="${line}">Remove</button>
            </div>
          </div>
          <div class="cart-item__price">
            ${compare}
            <strong>${escapeHTML(formatMoney(item.final_line_price))}</strong>
          </div>
        </article>`;
    }).join('');
  }

  async function changeCart(line, quantity) {
    const parsedQuantity = Number(quantity);
    const safeQuantity = Number.isFinite(parsedQuantity) ? Math.max(0, Math.floor(parsedQuantity)) : 0;
    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: safeQuantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update cart');
      state.cart = cart;
      renderCartDrawer(cart);
      renderCartPage(cart);
    } catch (error) {
      console.error(error);
      showToast(strings.error || 'Something went wrong. Please try again.');
    }
  }

  function updateBundleTotal(panel) {
    const addButton = $('[data-bundle-add]', panel);
    const totalEl = $('[data-bundle-total]', panel);
    if (!addButton || !totalEl) return;
    let total = Number(addButton.dataset.mainPrice || 0);
    $$('[data-bundle-checkbox]:checked', panel).forEach((checkbox) => {
      total += Number(checkbox.dataset.price || 0);
    });
    totalEl.textContent = formatMoney(total);
  }

  function setupBundle() {
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-bundle-checkbox]')) return;
      const panel = event.target.closest('[data-bundle-panel]');
      if (panel) updateBundleTotal(panel);
    });

    document.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-bundle-add]');
      if (!addButton) return;
      event.preventDefault();
      const panel = addButton.closest('[data-bundle-panel]');
      if (!panel) return;
      const items = [{ id: Number(addButton.dataset.mainVariantId), quantity: 1 }];
      $$('[data-bundle-checkbox]:checked', panel).forEach((checkbox) => {
        items.push({ id: Number(checkbox.dataset.variantId), quantity: 1 });
      });
      addMultipleToCart(items, addButton);
    });
  }

  function setupReorder() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-reorder]');
      if (!button) return;
      event.preventDefault();
      let items;
      try {
        items = JSON.parse(button.dataset.reorderItems || '[]');
      } catch (error) {
        items = [];
      }
      if (!items.length) return;
      addMultipleToCart(items, button);
    });
  }

  const COMPARE_KEY = 'pp_compare';
  const COMPARE_LIMIT = 3;
  const COMPARE_ATTRIBUTE_TAGS = ['beginner-friendly', 'beginner friendly', 'rechargeable', 'waterproof', 'quiet', 'couples', 'best for couples', 'body-safe silicone', 'travel-friendly', 'compact', 'easy to clean'];

  function getCompareList() {
    try { return JSON.parse(safeStorageGet(COMPARE_KEY) || '[]'); } catch (error) { return []; }
  }

  function setCompareList(list) {
    safeStorageSet(COMPARE_KEY, JSON.stringify(list));
    renderCompareTray();
  }

  function toggleCompare(handle) {
    if (!handle) return;
    const list = getCompareList();
    const index = list.indexOf(handle);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      if (list.length >= COMPARE_LIMIT) {
        showToast(`You can compare up to ${COMPARE_LIMIT} products at a time.`);
        return;
      }
      list.push(handle);
    }
    setCompareList(list);
  }

  function renderCompareTray() {
    const tray = $('[data-compare-tray]');
    const list = getCompareList();
    if (tray) {
      const textEl = $('[data-compare-tray-text]', tray);
      const openButton = $('[data-compare-open]', tray);
      tray.hidden = list.length === 0;
      if (openButton) openButton.disabled = list.length < 2;
      if (textEl) {
        textEl.textContent = list.length < 2
          ? `${list.length} selected — add ${2 - list.length} more to compare`
          : `${list.length} selected to compare`;
      }
    }
    $$('[data-compare-toggle]').forEach((button) => {
      const active = list.includes(button.dataset.compareHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function renderCompareGrid(content, products) {
    if (!products.length) {
      content.innerHTML = '<div class="empty-state"><h3>No products selected</h3><p>Add products to compare from any collection page.</p></div>';
      return;
    }
    content.innerHTML = `<div class="compare-grid">${products.map((product) => {
      const image = product.featured_image || (product.images && product.images[0]) || '';
      const price = formatMoney(product.price);
      const compareAt = product.compare_at_price > product.price ? `<s>${escapeHTML(formatMoney(product.compare_at_price))}</s> ` : '';
      const features = (product.tags || [])
        .filter((tag) => COMPARE_ATTRIBUTE_TAGS.includes(String(tag).toLowerCase()))
        .slice(0, 4)
        .map(escapeHTML)
        .join(', ');
      return `
        <article class="compare-card">
          <button class="compare-card__remove" type="button" data-compare-remove="${escapeHTML(product.handle)}" aria-label="Remove ${escapeHTML(product.title)} from comparison">×</button>
          <a class="compare-card__media" href="${escapeHTML(product.url)}">${image ? `<img src="${escapeHTML(image)}&width=400" alt="${escapeHTML(product.title)}" loading="lazy">` : ''}</a>
          <h3 class="compare-card__title"><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3>
          <p class="compare-card__price">${compareAt}<strong>${escapeHTML(price)}</strong></p>
          <dl class="compare-card__specs">
            <div><dt>Availability</dt><dd>${product.available ? 'In stock' : 'Sold out'}</dd></div>
            ${product.vendor ? `<div><dt>Brand</dt><dd>${escapeHTML(product.vendor)}</dd></div>` : ''}
            ${product.type ? `<div><dt>Type</dt><dd>${escapeHTML(product.type)}</dd></div>` : ''}
            <div><dt>Features</dt><dd>${features || '—'}</dd></div>
          </dl>
          <a class="button button--small button--full" href="${escapeHTML(product.url)}">View product</a>
        </article>`;
    }).join('')}</div>`;
  }

  async function openCompareDrawer() {
    const modal = $('#CompareDrawer');
    const content = $('[data-compare-content]', modal);
    if (!modal || !content) return;
    setModal(modal, true);
    const handles = getCompareList();
    if (!handles.length) {
      content.innerHTML = '<div class="empty-state"><h3>No products selected</h3><p>Add products to compare from any collection page.</p></div>';
      return;
    }
    content.innerHTML = '<div class="empty-state"><p>Loading comparison…</p></div>';
    try {
      const products = await Promise.all(handles.map((handle) =>
        fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      ));
      renderCompareGrid(content, products.filter(Boolean));
    } catch (error) {
      console.error(error);
      content.innerHTML = '<div class="empty-state"><p>Unable to load comparison right now.</p></div>';
    }
  }

  function setupCompare() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-compare-toggle]');
      if (toggle) {
        event.preventDefault();
        toggleCompare(toggle.dataset.compareHandle);
        return;
      }
      if (event.target.closest('[data-compare-clear]')) {
        setCompareList([]);
        return;
      }
      if (event.target.closest('[data-compare-open]')) {
        openCompareDrawer();
        return;
      }
      if (event.target.closest('[data-compare-close]')) {
        setModal($('#CompareDrawer'), false);
        return;
      }
      const remove = event.target.closest('[data-compare-remove]');
      if (remove) {
        toggleCompare(remove.dataset.compareRemove);
        openCompareDrawer();
      }
    });
  }

  function setupCart() {
    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-cart-open]');
      if (open) { event.preventDefault(); openCart(); return; }
      if (event.target.closest('[data-drawer-close]')) { closeCart(); return; }

      const quickAdd = event.target.closest('[data-quick-add]');
      if (quickAdd) {
        event.preventDefault();
        if (!quickAdd.dataset.variantId) {
          showToast(strings.error || 'Something went wrong. Please try again.');
          return;
        }
        const form = new FormData();
        form.append('id', quickAdd.dataset.variantId);
        form.append('quantity', '1');
        addToCart(form, quickAdd);
        return;
      }

      const cartChange = event.target.closest('[data-cart-change]');
      if (cartChange) {
        const input = $(`[data-cart-quantity-input][data-line="${cartChange.dataset.line}"]`);
        const current = Number(input?.value || 0);
        changeCart(cartChange.dataset.line, cartChange.dataset.cartChange === 'plus' ? current + 1 : current - 1);
        return;
      }

      const remove = event.target.closest('[data-cart-remove]');
      if (remove) changeCart(remove.dataset.line, 0);
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-quantity-input]')) changeCart(event.target.dataset.line, event.target.value);
    });
  }

  function getSelectedOptions(section) {
    return $$('[data-option-position]', section).map((fieldset) => $('[data-option-value]:checked', fieldset)?.value);
  }

  function updateProduct(section, variant) {
    const idInput = $('[data-variant-id-input]', section);
    const addButton = $('[data-add-to-cart-button]', section);
    const addText = $('[data-add-to-cart-text]', section);
    const addPrice = $('[data-add-to-cart-price]', section);
    const stickyButton = $('[data-sticky-add-button]', section);
    const stickyText = $('[data-sticky-add-text]', section);
    const currentPrice = $('[data-product-price]', section);
    const comparePrice = $('[data-compare-price]', section);
    const skuWrap = $('[data-product-sku]', section);
    const inventory = $('[data-inventory-status]', section);
    const inventoryText = $('[data-inventory-text]', section);
    const stickyPrice = $('[data-sticky-price]', section);
    const stickyVariant = $('[data-sticky-variant]', section);
    const lowStockThreshold = Number(section.dataset.lowStockThreshold || 5);

    if (!variant) {
      if (idInput) idInput.value = '';
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = strings.unavailable || 'Unavailable';
      if (addPrice) addPrice.textContent = '';
      if (stickyButton) stickyButton.disabled = true;
      if (stickyText) stickyText.textContent = strings.unavailable || 'Unavailable';
      if (inventory) inventory.classList.remove('is-available');
      if (inventoryText) inventoryText.textContent = strings.unavailable || 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    if (comparePrice) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      comparePrice.classList.toggle('hidden', !onSale);
      comparePrice.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
    }
    if (skuWrap) {
      skuWrap.hidden = !variant.sku;
      const value = $('span', skuWrap);
      if (value) value.textContent = variant.sku || '';
    }

    const available = Boolean(variant.available);
    if (addButton) addButton.disabled = !available;
    if (stickyButton) stickyButton.disabled = !available;
    if (stickyText) stickyText.textContent = available ? 'Add to bag' : (strings.soldOut || 'Sold out');
    if (addText) addText.textContent = available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
    if (addPrice) addPrice.textContent = available ? `— ${formatMoney(variant.price)}` : '';
    if (inventory) inventory.classList.toggle('is-available', available);
    if (inventoryText) {
      const quantity = Number(variant.inventory_quantity);
      inventoryText.textContent = available
        ? (Number.isFinite(quantity) && quantity > 0 && quantity <= lowStockThreshold ? `Only ${quantity} left` : 'In stock and ready to order')
        : 'Out of stock';
    }

    if (stickyVariant) stickyVariant.textContent = variant.title && variant.title !== 'Default Title' ? `${variant.title} · ` : '';

    if (variant.featured_media?.id) {
      const thumb = $(`[data-media-id="${variant.featured_media.id}"]`, section);
      thumb?.click();
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function updateOptionAvailability(section, variants) {
    const selected = getSelectedOptions(section);
    $$('[data-option-position]', section).forEach((fieldset, fieldsetIndex) => {
      $$('[data-option-value]', fieldset).forEach((input) => {
        const possible = variants.some((variant) => variant.available && variant.options.every((value, optionIndex) => {
          if (optionIndex === fieldsetIndex) return value === input.value;
          if (optionIndex < fieldsetIndex) return !selected[optionIndex] || value === selected[optionIndex];
          return true;
        }));
        input.disabled = !possible;
        const label = section.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        label?.classList.toggle('is-unavailable', !possible);
      });
    });
  }

  function setupProductSections() {
    $$('[data-product-section]').forEach((section) => {
      const variantsScript = $('[data-product-variants]', section);
      const form = $('.product-form', section);
      if (!variantsScript || !form) return;
      let variants = [];
      try { variants = JSON.parse(variantsScript.textContent); } catch (error) { console.error(error); }
      updateOptionAvailability(section, variants);

      section.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-value]')) return;
        const fieldset = event.target.closest('[data-option-position]');
        const selected = $('[data-selected-option]', fieldset);
        if (selected) selected.textContent = event.target.value;
        const options = getSelectedOptions(section);
        const variant = variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
        updateProduct(section, variant);
        updateOptionAvailability(section, variants);
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const quantity = $('input[name="quantity"]', form);
        if (quantity && Number(quantity.value) < 1) quantity.value = 1;
        addToCart(new FormData(form), $('[data-add-to-cart-button]', form));
      });

      $$('[data-media-id]', section).forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const panel = $(`[data-media-panel="${thumb.dataset.mediaId}"]`, section);
          if (!panel) return;
          $$('[data-media-panel]', section).forEach((item) => {
            item.classList.toggle('is-active', item === panel);
            if (item !== panel) $$('video', item).forEach((video) => video.pause());
          });
          $$('[data-media-id]', section).forEach((item) => item.classList.toggle('is-active', item === thumb));
          $('video', panel)?.play().catch(() => {});
        });
      });

      const sticky = $('[data-sticky-add]', section);
      const submit = $('[data-add-to-cart-button]', section);
      $('[data-sticky-add-button]', section)?.addEventListener('click', () => submit?.click());
      if (sticky && submit) {
        const updateStickyVisibility = () => {
          const buttonPosition = submit.getBoundingClientRect();
          sticky.classList.toggle('is-visible', buttonPosition.bottom < 0);
        };

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(([entry]) => {
            sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
          }, { threshold: 0 });
          observer.observe(submit);
        } else {
          window.addEventListener('scroll', updateStickyVisibility, { passive: true });
          window.addEventListener('resize', updateStickyVisibility);
          updateStickyVisibility();
        }
      }
    });
  }

  function setupExpandableSummary() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-expandable-toggle]');
      if (!toggle) return;
      const summary = toggle.previousElementSibling;
      if (!summary || !summary.hasAttribute('data-expandable-summary')) return;
      if (summary.hasAttribute('data-collapsed')) {
        summary.removeAttribute('data-collapsed');
        toggle.textContent = 'Show less';
      } else {
        summary.setAttribute('data-collapsed', '');
        toggle.textContent = 'Read more';
      }
    });
  }

  function setupQuantities() {
    document.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus], [data-cart-qty-minus]');
      const plus = event.target.closest('[data-quantity-plus], [data-cart-qty-plus]');
      if (!minus && !plus) return;
      const selector = event.target.closest('.quantity-selector');
      const input = $('input[type="number"]', selector);
      if (!input) return;
      const minimum = Number(input.min || 0);
      input.value = Math.max(minimum, Number(input.value || minimum) + (plus ? 1 : -1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setupProductFinder() {
    const root = $('[data-product-finder]');
    if (!root) return;
    const section = root.closest('section');
    const configEl = $('[data-finder-config]', section);
    let config = {};
    try { config = JSON.parse(configEl?.textContent || '{}'); } catch (error) { config = {}; }

    const steps = $$('[data-finder-step]', root);
    const progress = $('[data-product-finder-progress]', root);
    const resultPanel = $('[data-finder-result]', root);
    const answers = {};

    const categoryLabels = {
      'for-her': 'For her',
      'for-him': 'For him',
      'for-couples': 'For couples',
      'best-sellers': 'Best sellers'
    };

    function updateProgress(activeIndex) {
      if (!progress) return;
      const total = steps.length + 1;
      progress.style.width = `${((activeIndex + 1) / total) * 100}%`;
    }

    function showStep(index) {
      resultPanel.hidden = true;
      steps.forEach((step, i) => { step.hidden = i !== index; });
      updateProgress(index);
    }

    function showResult() {
      steps.forEach((step) => { step.hidden = true; });
      updateProgress(steps.length);
      const heading = $('[data-finder-result-heading]', resultPanel);
      const text = $('[data-finder-result-text]', resultPanel);
      const link = $('[data-finder-result-link]', resultPanel);
      const beginnerLink = $('[data-finder-beginner-link]', resultPanel);
      const category = answers.category || 'best-sellers';
      const categoryUrl = (config.category && config.category[category]) || (config.category && config.category['best-sellers']) || '/collections/best-sellers';
      const typeUrl = config.type && config.type[answers.type];
      const resultUrl = typeUrl || categoryUrl;
      const resultLabel = typeUrl
        ? { vibrator: 'Vibrators', dildo: 'Dildos', mens: "Men's pleasure" }[answers.type]
        : categoryLabels[category] || 'Best sellers';

      const why = [];
      why.push(`Matched to ${categoryLabels[category] || 'best sellers'.toLowerCase()}${answers.type && typeUrl ? `, narrowed to ${resultLabel.toLowerCase()}` : ''}.`);
      if (answers.level === 'beginner') why.push('Look for products tagged "beginner-friendly" for simple controls and a manageable size.');
      if (answers.quiet === 'yes') why.push('Since quiet operation matters to you, check the noise level noted on each product page before ordering.');
      if (answers.power === 'rechargeable') why.push('Filter or check for "rechargeable" — more travel-friendly and no spare batteries needed.');
      if (answers.power === 'battery') why.push('Battery-operated options are usually a lower upfront cost and simple to replace.');
      if (answers.budget && answers.budget !== 'any') {
        const budgetLabel = { 'under-1000': 'under ₱1,000', '1000-3000': '₱1,000–3,000', '3000-plus': '₱3,000+' }[answers.budget];
        why.push(`Use the price filter on the results page to stay within ${budgetLabel}.`);
      }

      if (heading) heading.textContent = `Start with: ${resultLabel}`;
      if (text) text.textContent = why.join(' ');
      if (link) link.href = resultUrl;
      if (beginnerLink) {
        const showBeginner = answers.level === 'beginner' && config.beginnerFriendly && resultUrl !== config.beginnerFriendly;
        beginnerLink.hidden = !showBeginner;
        if (showBeginner) beginnerLink.href = config.beginnerFriendly;
      }
      resultPanel.hidden = false;
    }

    root.addEventListener('click', (event) => {
      const option = event.target.closest('[data-finder-answer]');
      if (option) {
        answers[option.dataset.finderAnswer] = option.dataset.finderValue;
        const currentIndex = steps.findIndex((step) => !step.hidden);
        if (currentIndex < steps.length - 1) {
          showStep(currentIndex + 1);
        } else {
          showResult();
        }
        return;
      }
      if (event.target.closest('[data-finder-restart]')) {
        Object.keys(answers).forEach((key) => delete answers[key]);
        showStep(0);
      }
    });

    showStep(0);
  }

  function setupSearch() {
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-open]')) { event.preventDefault(); openSearch(); }
      if (event.target.closest('[data-search-close]')) closeSearch();
    });

    const input = $('[data-predictive-search-input]');
    const results = $('[data-predictive-search-results]');
    if (!input || !results) return;
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        state.searchController?.abort();
        state.searchController = new AbortController();
        try {
          const url = `${routes.predictiveSearch}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide`;
          const response = await fetch(url, { signal: state.searchController.signal });
          if (!response.ok) throw new Error('Search failed');
          const data = await response.json();
          const products = data.resources?.results?.products || [];
          results.innerHTML = products.length ? `
            <div class="predictive-search__grid">${products.map((product) => `
              <a class="predictive-search__item" href="${escapeHTML(product.url)}">
                ${product.image ? `<img src="${escapeHTML(product.image)}&width=160" alt="${escapeHTML(product.title)}" width="72" height="86" loading="lazy">` : ''}
                <div><h3>${escapeHTML(product.title)}</h3><p>${escapeHTML(product.price || '')}</p></div>
              </a>`).join('')}</div>
            <div class="predictive-search__footer"><a class="text-link" href="${routes.search}?q=${encodeURIComponent(query)}&type=product&options%5Bunavailable_products%5D=hide">View all results →</a></div>`
            : '<div class="empty-state"><p>No matching products yet.</p></div>';
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }, 220);
    });
  }

  function setupInStockCollections() {
    const section = $('[data-enforce-in-stock]');
    if (!section || window.Shopify?.designMode) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('filter.v.availability') === '1') return;
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.delete('page');
    window.location.replace(url.toString());
  }

  function setupFilters() {
    const form = $('#CollectionFiltersForm');
    const results = $('[data-collection-results]');

    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-filter-open]');
      if (open) setModal($('[data-filter-drawer]'), true);
      if (event.target.closest('[data-filter-close]')) setModal($('[data-filter-drawer]'), false);
    });

    if (!form || !results) return;

    async function loadResults(url, { push = true, focus = false } = {}) {
      results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = $('[data-collection-results]', parsed);
        if (!next) { window.location.href = url; return; }
        results.innerHTML = next.innerHTML;
        if (push) window.history.pushState({ url }, '', url);
        if (focus) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        results.removeAttribute('aria-busy');
      }
    }

    function currentFormUrl() {
      const params = new URLSearchParams(new FormData(form)).toString();
      return `${window.location.pathname}${params ? `?${params}` : ''}`;
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      loadResults(currentFormUrl(), { focus: true });
      setModal($('[data-filter-drawer]'), false);
    });

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) {
        loadResults(currentFormUrl(), { focus: false });
      } else if (event.target.matches('[data-sort-select]')) {
        loadResults(currentFormUrl(), { focus: true });
      }
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('#CollectionResults a[href]');
      if (!link) return;
      let url;
      try { url = new URL(link.href, window.location.origin); } catch (error) { return; }
      if (url.pathname !== window.location.pathname || link.target === '_blank') return;
      event.preventDefault();
      loadResults(link.href, { focus: true });
    });

    window.addEventListener('popstate', () => {
      loadResults(window.location.href, { push: false });
    });
  }

  function setupRecommendations() {
    $$('[data-product-recommendations], [data-product-bundle]').forEach(async (element) => {
      const selector = element.hasAttribute('data-product-bundle') ? '[data-product-bundle]' : '[data-product-recommendations]';
      if (!element.dataset.url || element.innerHTML.trim()) return;
      try {
        const response = await fetch(element.dataset.url);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = $(selector, parsed);
        if (replacement?.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
      } catch (error) { console.error(error); }
    });
  }

  function safeStorageGet(key) {
    try { return window.localStorage.getItem(key); } catch (error) { return null; }
  }

  function safeStorageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (error) { /* Storage may be blocked. */ }
  }

  function setupAgeGate() {
    const gate = $('#AgeGate');
    if (!gate || safeStorageGet('pp_age_confirmed') === 'yes') return;
    setModal(gate, true);
    $('[data-age-confirm]', gate)?.addEventListener('click', () => {
      safeStorageSet('pp_age_confirmed', 'yes');
      setModal(gate, false);
      document.dispatchEvent(new CustomEvent('pp:age-confirmed'));
    });
  }

  function movePopupStorageKey(popup) {
    return `pp_move_popup_${popup?.dataset.campaignKey || 'default'}_seen`;
  }

  function readCookie(name) {
    return document.cookie.split('; ').some((entry) => entry.split('=')[0] === name);
  }

  function writeCookie(name) {
    document.cookie = `${name}=yes; Max-Age=315360000; Path=/; SameSite=Lax`;
  }

  function closeMovePopup(popup, remember = true) {
    if (!popup) return;
    if (remember && !window.Shopify?.designMode) writeCookie(movePopupStorageKey(popup));
    setModal(popup, false);
  }

  function setupMovePopup() {
    const popup = $('[data-move-popup]');
    if (!popup) return;
    const storageKey = movePopupStorageKey(popup);
    const alreadySeen = !window.Shopify?.designMode && readCookie(storageKey);
    if (alreadySeen) return;

    const showPopup = () => {
      if ($('.age-gate.is-open')) return;
      setModal(popup, true);
      setTimeout(() => $('[data-move-popup-close]', popup)?.focus(), 80);
    };
    const delay = Math.max(0, Number(popup.dataset.delay || 0));
    const schedule = () => window.setTimeout(showPopup, delay);

    if ($('.age-gate.is-open')) document.addEventListener('pp:age-confirmed', schedule, { once: true });
    else schedule();

    $$('[data-move-popup-close]', popup).forEach((button) => button.addEventListener('click', () => closeMovePopup(popup, true)));
    $('[data-move-popup-visit]', popup)?.addEventListener('click', () => writeCookie(storageKey));
  }

  function setupNavigation() {
    $$('.mobile-nav').forEach((nav) => nav.addEventListener('toggle', () => lockBody()));
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-mobile-nav-close]')) event.target.closest('.mobile-nav')?.removeAttribute('open');
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeCart(); closeSearch(); setModal($('[data-filter-drawer]'), false);
      setModal($('#CompareDrawer'), false);
      closeMovePopup($('.move-popup.is-open'), true);
      $('.mobile-nav[open]')?.removeAttribute('open');
    });
  }

  async function setupGoogleReviews() {
    const root = $('[data-google-reviews]');
    if (!root) return;
    const placeId = root.dataset.placeId;
    const apiKey = root.dataset.apiKey;
    if (!placeId || !apiKey) return;

    const starMarkup = (rating) => {
      let html = '';
      for (let i = 1; i <= 5; i += 1) {
        html += `<span${rating >= i ? ' class="is-filled"' : ''}>★</span>`;
      }
      return html;
    };

    try {
      const response = await fetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}`, {
        headers: {
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'rating,userRatingCount,reviews,googleMapsUri',
        },
      });
      if (!response.ok) throw new Error('Unable to load Google reviews');
      const data = await response.json();

      const rating = data.rating;
      const count = data.userRatingCount;
      if (rating && count) {
        const scoreEl = $('[data-google-score]', root);
        const starsEl = $('[data-google-stars]', root);
        const countEl = $('[data-google-count]', root);
        if (scoreEl) scoreEl.textContent = rating.toFixed(1);
        if (starsEl) starsEl.innerHTML = starMarkup(rating);
        if (countEl) countEl.textContent = `${count} Google reviews`;
      }

      const reviews = data.reviews || [];
      if (reviews.length) {
        const list = $('[data-google-reviews-list]', root);
        if (list) {
          list.innerHTML = reviews.map((review) => {
            const name = review.authorAttribution?.displayName || 'Google user';
            const photo = review.authorAttribution?.photoUri;
            const text = review.text?.text || review.originalText?.text || '';
            const time = review.relativePublishTimeDescription || '';
            const initial = escapeHTML(name.slice(0, 1).toUpperCase());
            const avatar = photo
              ? `<img class="google-review-row__avatar-img" src="${escapeHTML(photo)}" alt="" loading="lazy" width="36" height="36">`
              : `<span class="google-review-row__avatar" aria-hidden="true">${initial}</span>`;
            return `
              <article class="google-review-row" role="listitem">
                ${avatar}
                <div class="google-review-row__body">
                  <div class="google-review-row__meta">
                    <strong>${escapeHTML(name)}</strong>
                    <span class="google-review-row__stars">${starMarkup(review.rating || 0)}</span>
                  </div>
                  <p class="google-review-row__quote">${escapeHTML(text)}</p>
                  ${time ? `<small class="google-review-row__time">${escapeHTML(time)}</small>` : ''}
                </div>
              </article>`;
          }).join('');
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    setupCart();
    setupExpandableSummary();
    setupProductFinder();
    setupQuantities();
    setupProductSections();
    setupSearch();
    setupInStockCollections();
    setupFilters();
    setupRecommendations();
    setupAgeGate();
    setupMovePopup();
    setupNavigation();
    setupGoogleReviews();
    setupBundle();
    setupReorder();
    setupCompare();
    renderCompareTray();
    refreshCart();
  });
})();
